import React, {useEffect, useState, Fragment} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import {useTranslation} from 'react-i18next';
import TableToolbar from '../../components/Table/toolbar';
import LoadMore from '../../components/Table/load-more';
import EnhancedTableHead from '../../components/Table/enhanced-table-head';
import {useDispatch, useSelector} from 'react-redux';
import {isAdmin} from '../../services/auth/auth-service';
import SearchBar from '../../components/Filters/search-bar';
import Moment from 'react-moment';
import moment from 'moment';
import {getEvents, getEventTypes} from '../../services/api/event-api-service';
import {alert} from '../../redux/ducks/notification';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    marginBottom: theme.spacing(3),
    overflow: 'hidden',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    minWidth: 650,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  payload: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    fontSize: '0.75rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  }
}));

const Events = ({history}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);

  useEffect(() => {
    if (null === auth.token) {
      history.push('/')
    } else if (!isAdmin(auth.role)) {
      history.push('/dashboard/main')
    }
  }, []); // eslint-disable-line

  const [events, setEvents] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [next, setNext] = useState(undefined);
  const [selector, setSelector] = useState('');
  const [value, setValue] = useState('');
  const [from, setFrom] = useState(moment());
  const [to, setTo] = useState(moment());
  const [type, setType] = useState('');
  const [eventTypes, setEventTypes] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const [order, setOrder] = useState({
    column: 'createdAt',
    direction: 'desc'
  });

  const headCells = [
    {id: 'createdAt', label: t('tables.events.thead.created_at'), sortable: true},
    {id: 'user', label: t('tables.events.thead.user'), sortable: true},
    {id: 'type', label: t('tables.events.thead.type'), sortable: true},
    {id: 'entity', label: t('tables.events.thead.entity'), sortable: true},
    {id: 'path', label: t('tables.events.thead.path'), sortable: false},
    {id: 'ip', label: t('tables.events.thead.ip'), sortable: false},
    {id: 'payload', label: t('tables.events.thead.payload'), sortable: false},
  ];

  const fields = [
    {value: 'user', name: t('tables.events.thead.user')},
    {value: 'entity', name: t('tables.events.thead.entity')},
    {value: 'search', name: t('tables.events.thead.search')},
    {value: 'createdAt', name: t('tables.events.thead.created_at')},
  ];

  const handleRequestSort = (event, property) => {
    const isAsc = order.column === property && order.direction === 'asc';
    setOrder(prevState => {
      return {
        ...prevState,
        column: property,
        direction: isAsc ? 'desc' : 'asc'
      }
    })
  };

  const buildFilters = () => {
    let filters = `&sort=${order.column}&order=${order.direction}`;

    switch (selector) {
      case 'user':
        filters += `&user=${value}`;
        break;
      case 'entity':
        filters += `&entity=${value}`;
        break;
      case 'search':
        filters += `&search=${value}`;
        break;
      case 'createdAt':
        filters += `&createdAfter=${moment(from).format('Y-MM-DD')}&createdBefore=${moment(to).format('Y-MM-DD')}`;
        break;
      default:
        filters += ''
    }

    if (type.length > 0) {
      filters += `&type=${type}`
    }

    return filters;
  };

  const fetchEvents = async (filters = null, loadMore = false) => {
    if (null === filters) {
      filters = buildFilters();
    }

    try {
      const response = await getEvents(filters);
      const data = response.data;
      setEvents(loadMore ? events.concat(data['items']) : data['items']);
      setTotalItems(data['meta'].total);
      setNext(true === data['meta'].hasNext ? `${filters.replace(/&page=\d+$/, '')}&page=${data['meta'].page + 1}` : undefined);
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await getEventTypes();
      setEventTypes(response.data.map(eventType => ({
        value: eventType,
        name: `events.types.${eventType}`
      })));
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [order]); // eslint-disable-line

  useEffect(() => {
    fetchEventTypes();
  }, []); // eslint-disable-line

  const handleFiltersSubmit = event => {
    event.preventDefault();
    let filters = buildFilters();
    fetchEvents(filters);
  };

  const handleSelector = event => {
    setSelector(event.target.value);
  };

  const handleValue = event => {
    setValue(event.target.value);
  };

  const handleFrom = data => {
    setFrom(moment(data))
  };

  const handleTo = data => {
    setTo(moment(data))
  };

  const handleType = event => {
    setType(event.target.value);
  };

  const content = (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableToolbar name={`${t('tables.events.name')} (${totalItems})`}/>
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} size="small">
            <EnhancedTableHead
              order={order.direction}
              orderBy={order.column}
              onRequestSort={handleRequestSort}
              headCells={headCells}
            />
            <TableBody>
              {events.map((row) => (
                <Fragment key={row.id}>
                  <TableRow hover>
                    <TableCell>
                      <Moment format={'DD-MM-YYYY HH:mm'}>
                        {row.createdAt}
                      </Moment>
                    </TableCell>
                    <TableCell>{row.user ? row.user.name || row.user.email : '—'}</TableCell>
                    <TableCell>{t(`events.types.${row.type}`, {defaultValue: row.type})}</TableCell>
                    <TableCell>{row.entity ? t(`events.entities.${row.entity}`, {defaultValue: row.entity}) : '—'}</TableCell>
                    <TableCell>{row.path}</TableCell>
                    <TableCell>{row.ip || '—'}</TableCell>
                    <TableCell>
                      {row.payload ? (
                        <IconButton
                          size="small"
                          onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                          aria-label="expand payload"
                        >
                          {expandedId === row.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                  {row.payload && (
                    <TableRow>
                      <TableCell colSpan={headCells.length} style={{paddingBottom: 0, paddingTop: 0, borderBottom: expandedId === row.id ? undefined : 0}}>
                        <Collapse in={expandedId === row.id} timeout="auto" unmountOnExit>
                          <pre className={classes.payload}>
                            {JSON.stringify(row.payload, null, 2)}
                          </pre>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {next &&
          <LoadMore buttonText={t('tables.common.load_more')} loadMore={() => fetchEvents(next, true)}/>
        }
      </Paper>
    </div>
  );

  return (
    <Fragment>
      <SearchBar
        from={from}
        handleFrom={handleFrom}
        to={to}
        handleTo={handleTo}
        handleSubmit={handleFiltersSubmit}
        selector={selector}
        handleSelector={handleSelector}
        fields={fields}
        value={value}
        handleValue={handleValue}
        status={type}
        handleStatus={handleType}
        statuses={eventTypes}
        statusLabel={t('filters.type.value')}
      />
      {totalItems > 0 ? content : (
        <Paper className={classes.emptyState}>
          <Typography variant="body1" color="textSecondary">
            {t('tables.common.no_data')}
          </Typography>
        </Paper>
      )}
    </Fragment>
  );
};

export default Events;
